import { useTranslation } from 'react-i18next';

import type { SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import { annualPriceLabel, formatAmount, formatLongDate, priceLabelFor } from '~/utils/trialPricing';
import { isCancelScheduled } from '~/utils/keepOffer';
import { trialGoalLine } from '~/types/client/trialGoal';
import type { TrialGoal } from '~/types/client/trialGoal';

import styles from './TrialStatusCard.module.css';

// Durée par défaut ; un essai parrainé dure 30 jours (stripe-b2b referral.js) → lue sur les dates Stripe.
const DEFAULT_TRIAL_DAYS = 14;
const DAY_MS = 86_400_000;

function IconCheck() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M20 6 9 17l-5-5" />
        </svg>
    );
}

/**
 * Bloc « Essai gratuit » de la page Mon compte : jour X/14, jalons (début,
 * rappel J+7, premier prélèvement) et annulation programmée le cas échéant.
 * Rendu uniquement pendant l'essai (`status === 'trialing'`), au-dessus du
 * bouton « Gérer ou annuler mon abonnement ». Décision 15/09/2026 : le badge
 * vit ici, pas sur l'accueil TV (vu par les clients du bar).
 */
export function TrialStatusCard({ sub, trialGoal }: { sub: SubscriptionStatus; trialGoal?: TrialGoal | null }) {
    const { t, i18n } = useTranslation();
    if (sub.status !== 'trialing' || !sub.trialEnd) return null;

    const lang = i18n.language;
    const now = Date.now();
    const endMs = sub.trialEnd * 1000;
    const startMs = sub.trialStart ? sub.trialStart * 1000 : endMs - DEFAULT_TRIAL_DAYS * DAY_MS;
    const totalDays = Math.max(1, Math.round((endMs - startMs) / DAY_MS));
    const day = Math.min(totalDays, Math.max(1, Math.floor((now - startMs) / DAY_MS) + 1));
    const reminderMs = endMs - 7 * DAY_MS;

    const startDate = formatLongDate(new Date(startMs), lang);
    const reminderDate = formatLongDate(new Date(reminderMs), lang);
    const stopping = isCancelScheduled(sub);
    // Arrêt programmé par date avant la fin d'essai : c'est cette date que l'accès s'arrête.
    const accessEndMs = stopping && sub.cancelAt && sub.cancelAt * 1000 < endMs ? sub.cancelAt * 1000 : endMs;
    const endDate = formatLongDate(new Date(accessEndMs), lang);
    // Montant réel de la prochaine facture (coupons partenaires compris) ;
    // repli sur le prix catalogue de la devise si le backend ne l'a pas.
    // Essai d'un abonnement annuel (choisi à l'inscription) : « 490 €/an », jamais « /mois ».
    const yearly = sub.interval === 'year';
    const catalogPrice = yearly ? annualPriceLabel(sub.currency) : priceLabelFor(sub.currency);
    const price =
        typeof sub.nextAmount === 'number' && sub.currency
            ? formatAmount(sub.nextAmount, sub.currency, lang)
            : catalogPrice;

    const lastDays = accessEndMs - now <= 3 * DAY_MS;
    // Objectif « 3 soirées » (groupe traité seulement) : une seule ligne en plus.
    const goalLine = trialGoalLine(trialGoal);
    const reminderSent = now >= reminderMs;

    return (
        <div className={styles.trial}>
            <div className={styles.head}>
                <span className={styles.title}>{t('account.trial.title')}</span>
                <span className={styles.day}>{t('account.trial.dayOf', { day, total: totalDays })}</span>
            </div>
            <div className={styles.bar}>
                <div
                    className={`${styles.fill} ${lastDays ? styles.fillWarn : ''}`}
                    style={{ width: `${(day / totalDays) * 100}%` }}
                />
            </div>

            {goalLine && (
                <p className={styles.goal}>
                    {t(goalLine.key, {
                        ...goalLine.values,
                        idea: goalLine.idea ? t(`trialGoal.idea.${goalLine.idea}`) : undefined,
                    })}
                </p>
            )}

            <ul className={styles.rows}>
                <li className={styles.row}>
                    <span className={`${styles.dot} ${styles.dotDone}`}>
                        <IconCheck />
                    </span>
                    <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{t('account.trial.started')}</span>
                        <span className={styles.rowDate}>{startDate}</span>
                    </span>
                </li>
                <li className={styles.row}>
                    <span className={`${styles.dot} ${reminderSent ? styles.dotDone : styles.dotNext}`}>
                        {reminderSent && <IconCheck />}
                    </span>
                    <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{t('account.trial.reminder')}</span>
                        <span className={styles.rowDate}>{reminderDate}</span>
                    </span>
                </li>
                <li className={styles.row}>
                    <span className={`${styles.dot} ${lastDays ? styles.dotWarn : styles.dotLater}`} />
                    <span className={styles.rowText}>
                        <span className={styles.rowTitle}>
                            {stopping
                                ? t('account.trial.accessUntil')
                                : t(yearly ? 'account.trial.startsYear' : 'account.trial.starts', { price })}
                        </span>
                        <span className={`${styles.rowDate} ${lastDays ? styles.rowDateWarn : ''}`}>{endDate}</span>
                    </span>
                </li>
            </ul>

            <p className={styles.note}>
                {stopping ? t('account.trial.cancelScheduled') : t('account.trial.nothingToDo', { date: endDate })}
            </p>
        </div>
    );
}
